import { ArrowCounterClockwiseIcon, ImageIcon, PaintBucketIcon, TrashSimpleIcon, UploadSimpleIcon } from '@phosphor-icons/react';
import FormField from 'components/FormElements/FormField';
import FormFieldDescription from 'components/FormElements/FormFieldDescription';
import FormFieldError from 'components/FormElements/FormFieldError';
import FormLabel from 'components/FormElements/FormLabel';
import Modal, { ModalWidth } from 'components/Modal';
import UploadArea from 'components/Upload/UploadArea';
import { useStorroApi } from 'context/StorroApiContext';
import { INPUT_WARNING_CLASSNAMES } from 'helpers/CssClasses';
import invert from 'invert-color';
import React, { JSX, useEffect, useMemo, useRef, useState } from 'react';
import { ColorResult, TwitterPicker } from 'react-color';
import { useForm, useWatch } from 'react-hook-form';
import formatErrorMessage from 'util/FormatErrorMessage';
import { logger } from 'util/Logger';
import ImageActionButton from './ImageActionButton';
import FormInput from 'components/FormElements/FormInput';
import classNames from 'classnames';
import { HEX_REGEX, isHex } from 'util/Validators';
import Form from 'components/FormElements/Form';
import useRealm from 'hooks/realm/UseRealm';
import { Realm } from 'types';
import useTheme from 'hooks/UseTheme';
import { Trans, useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import ErrorSection from 'components/Alert/ErrorSection';
import useFormError from 'hooks/useFormError';
import { TFunction } from 'i18next';
import { z } from 'zod';

const DEFAULT_COLOR = '#2563eb';
const DEFAULT_COLORS = ['#ff6900', '#fcb900', '#00d084', '#8ed1fc', '#0693e3', '#abb8c3', '#eb144c', '#f78da7', '#aa7942', DEFAULT_COLOR];
const ALLOWED_MIME_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];
const MAX_SIZE = 5000; // in kb

const createSchema = (t: TFunction) =>
  z.object({
    color: z
      .string()
      .min(1, t('please-provide-a-valid-color', 'Please provide a valid color'))
      .regex(HEX_REGEX, t('invalid-color-code', 'invalid color code')),
    logo: z
      .custom<FileList | null | undefined>()
      .refine(files => !files || !files[0] || ALLOWED_MIME_TYPES.includes(files[0].type), {
        message: t('only-jpg-or-png-is-allowed', 'Only JPG or PNG is allowed'),
      })
      .refine(files => !files || !files[0] || files[0].size / 1000 < MAX_SIZE, {
        message: t('logo-should-be-less-than-[size]kb', 'Logo should be less than {{size}}kb', { size: MAX_SIZE }),
      }),
  });

type SchemaType = z.infer<ReturnType<typeof createSchema>>;

interface Props {
  isVisble: boolean;
  onRequesCloseModal: () => void;
  onSuccess: (realm: Realm) => Promise<void> | void;
}

function BrandRealmModal({ isVisble, onRequesCloseModal, onSuccess }: Props): JSX.Element {
  const [showColorPicker, setShowColorPicker] = useState<boolean>(false);
  const [realmColor, setRealmColor] = useState<string>(DEFAULT_COLOR);
  const [invertColor, setInvertColor] = useState<string>(invert(DEFAULT_COLOR, true));
  const [logoHasChanged, setLogoHasChanged] = useState<boolean>(false);

  const hiddenInputUploadRef = useRef<HTMLInputElement>(null);

  const { realm, realmCustomLogo } = useRealm();
  const { storroApi } = useStorroApi();
  const { companyName } = useTheme();
  const { t } = useTranslation();

  const schema = useMemo(() => createSchema(t), [t]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    control,
    reset,
  } = useForm<SchemaType>({
    resolver: zodResolver(schema),
    reValidateMode: 'onChange',
  });
  const { fieldError, nonFieldErrors, setGeneralError } = useFormError(schema, errors);

  const logoFormValue = useWatch({
    control,
    name: 'logo',
  });
  const colorFormValue = useWatch({
    control,
    name: 'color',
  });
  const colorFormValueChanged = (realm?.color ?? DEFAULT_COLOR) !== colorFormValue;

  // as we need the ref for the file input, we deconstruct the object here
  const { ref: registerLogoRef, ...registerLogo } = register('logo');

  /**
   * Flag to indicate if the value is a valid uploaded logo
   */
  const hasValidUploadedFile =
    logoFormValue !== null && logoFormValue !== undefined && logoFormValue.length > 0 && ALLOWED_MIME_TYPES.includes(logoFormValue[0].type);
  const hasUploadedFile = logoFormValue !== null && logoFormValue !== undefined && logoFormValue.length > 0;

  /**
   * Get the logo, either it is the custom logo that the user
   * already uploaded, or it is the newly uploaded logo or we return undefined
   * if the user marked the files as deleted.
   */
  const logoUrl = useMemo((): string | undefined => {
    if (hasValidUploadedFile) {
      return URL.createObjectURL(logoFormValue[0]);
    }

    // the user removed the logo
    if (logoFormValue === null) {
      return undefined;
    }

    if (realmCustomLogo) {
      return realmCustomLogo;
    }
  }, [hasValidUploadedFile, logoFormValue, realmCustomLogo]);

  /**
   * Submitting the form
   */
  const onSubmit = async ({ color, logo }: SchemaType) => {
    if (!realm) return;

    try {
      const updatedValues: { color: string | undefined; logo: File | undefined } = {
        color: undefined,
        logo: undefined,
      };

      if (logo && logo[0]) {
        updatedValues.logo = logo[0];
      }

      if (colorFormValueChanged) {
        updatedValues.color = color;
      }

      // update the branding
      await storroApi.updateRealmBranding(realm.id, updatedValues);

      // as we use the FormData api to set the file
      // are unable to reset the logo via the FormData as FormData cannot be null
      // Therefor, if the logo is null (basiclly a reset), we should fire a
      // separated call to the API to remove the logo
      if (logo === null) {
        await storroApi.removeRealmLogo(realm.id);
      }

      await onSuccess(realm);

      onRequesCloseModal();
    } catch (error) {
      setGeneralError(formatErrorMessage('update Realm branding', error instanceof Error ? error.message : undefined));
      logger.error(error);
    }
  };

  /**
   * Open the file selector
   */
  const showFileSelector = () => hiddenInputUploadRef.current?.click();

  const onClosed = () => {
    reset({ color: realm?.color, logo: undefined });
    setShowColorPicker(false);
  };

  /**
   * Callback from the colorpicker when a value changed.
   */
  const onColorChange = (color: ColorResult) => {
    setValue('color', color.hex, { shouldValidate: true, shouldDirty: true });
  };

  /**
   * Remove the realm logo
   */
  const removeLogo = async () => {
    setValue('logo', null);
    setLogoHasChanged(true);
  };

  /**
   * Save action
   */
  const action = useMemo(() => {
    return {
      label: 'Save',
      loading: isSubmitting,
      formId: 'update-realm-branding',
    };
  }, [isSubmitting]);

  /**
   * Update the logoHasChanged flag when we have a valid uploaded file
   */
  useEffect(() => {
    setLogoHasChanged(hasValidUploadedFile);
  }, [hasValidUploadedFile]);

  /**
   * When the color is changing, change the invert and realmColor as well
   */
  useEffect(() => {
    if (colorFormValue && isHex(colorFormValue)) {
      setInvertColor(invert(colorFormValue, true));
      setRealmColor(colorFormValue);
    }
  }, [colorFormValue]);

  /**
   * Set the default values when the realm is loaded
   */

  useEffect(() => {
    if (isVisble && realm) {
      reset(formValues => ({ ...formValues, color: realm.color ?? DEFAULT_COLOR }));
      setRealmColor(realm.color ?? DEFAULT_COLOR);
    }
  }, [isVisble, realm, reset]);

  return (
    <Modal
      title={t('brand-your-realm', 'Brand your realm')}
      isVisible={isVisble}
      onRequestCloseModal={onRequesCloseModal}
      action={action}
      onClosed={onClosed}
      className='min-h-[15rem]'
      helpText={`* ${t('required-fields', 'Required fields')}`}
      width={ModalWidth.md}
    >
      <ErrorSection errors={nonFieldErrors} />
      <Form id='update-realm-branding' onSubmit={handleSubmit(onSubmit)}>
        <FormField>
          <FormLabel htmlFor='color'>{t('primary-color', 'Primary Color')}</FormLabel>
          <FormFieldDescription>
            {t('primary-color-description', 'This is the primary color, used for buttons, links, headers etc.')}
          </FormFieldDescription>
          <div className='flex items-center gap-x-1'>
            <FormInput
              hasError={fieldError('color') !== undefined}
              id='color'
              className={classNames({
                'text-gray-500': !colorFormValueChanged,
              })}
              {...register('color')}
            />
            <div className='p-1 h-8 w-9 border rounded cursor-pointer' onClick={() => setShowColorPicker(true)}>
              <div style={{ backgroundColor: realmColor }} className='w-full h-full rounded flex justify-center items-center'>
                <PaintBucketIcon size={16} color={invertColor} />
              </div>
            </div>
          </div>

          {showColorPicker && (
            <>
              <div className='fixed top-0 right-0 bottom-0 left-0' onClick={() => setShowColorPicker(false)} />
              <div className='absolute right-0 top-[90px]'>
                <TwitterPicker triangle='top-right' colors={DEFAULT_COLORS} color={realmColor} onChangeComplete={onColorChange} />
              </div>
            </>
          )}

          {fieldError('color') && <FormFieldError>{fieldError('color')}</FormFieldError>}
        </FormField>
        <FormField>
          <FormLabel htmlFor='logo'>{'Logo'}</FormLabel>
          <FormFieldDescription>
            {t(
              'logo-description',
              'The logo will be placed in the upper-left corner of the page and will replace the default {{companyName}} logo.',
              { companyName },
            )}
          </FormFieldDescription>

          <UploadArea
            onSelected={files => {
              const fileList = new DataTransfer();
              fileList.items.add(files[0]);
              setValue('logo', fileList.files);
            }}
            className={classNames('flex flex-col gap-2 items-center justify-center bg-gray-50 p-10 border border-gray-200 rounded-sm', {
              [INPUT_WARNING_CLASSNAMES]: fieldError('logo'),
            })}
          >
            {logoUrl && <img src={logoUrl} width={150} />}
            {!logoUrl && (
              <div className='flex flex-col justify-center items-center p-5 border rounded-md cursor-pointer' onClick={showFileSelector}>
                <ImageIcon className='text-gray-400' size={80} />
                <span className='text-xs text-gray-400 italic text-center'>
                  <Trans
                    i18nKey='no-logo-selected'
                    defaults='No logo selected, <br /> default {{companyName}} logo will be used'
                    values={{
                      companyName,
                    }}
                  />
                </span>
              </div>
            )}

            <div className='flex gap-x-2'>
              <ImageActionButton
                icon={<UploadSimpleIcon />}
                toolTip={t('upload-custom-logo', 'Upload your logo')}
                onClick={showFileSelector}
              />

              <ImageActionButton
                disabled={!realmCustomLogo || logoFormValue === null}
                icon={<TrashSimpleIcon />}
                toolTip={t('remove-custom-logo', 'Remove your custom logo')}
                onClick={removeLogo}
              />

              <ImageActionButton
                disabled={!logoHasChanged}
                icon={<ArrowCounterClockwiseIcon />}
                toolTip={t('reset-logo', 'Reset the logo to the default logo')}
                onClick={() => setValue('logo', undefined)}
              />
            </div>

            {!hasValidUploadedFile && hasUploadedFile && (
              <p className='text-red-500 italic text-sm'>{t('invalid-file', 'You have selected an invalid file to upload')}</p>
            )}
          </UploadArea>
          <input
            className='hidden'
            id='logo'
            type='file'
            accept='image/*'
            ref={e => {
              registerLogoRef(e);
              hiddenInputUploadRef.current = e;
            }}
            {...registerLogo}
          />
          {fieldError('logo') && <FormFieldError>{fieldError('logo')}</FormFieldError>}
        </FormField>
      </Form>
    </Modal>
  );
}

export default BrandRealmModal;
